import { HttpErrorResponse } from "@angular/common/http";
import { errorResponse } from "src/app/interfaces/errorResponse";

const base = "serverConnections.errors.";

/** Mirrors `KeycloakClientErrorCodes` in user-app `keycloak.enum.ts`. */
export const KEYCLOAK_CLIENT_ERROR_CODES = [
  "createFailed",
  "updateFailed",
  "clientIdAlreadyExists",
  "connectionNameAlreadyExists",
  "alreadyExists",
  "conflict",
] as const;

export type KeycloakClientErrorCode = (typeof KEYCLOAK_CLIENT_ERROR_CODES)[number];
export type ServerConnectionErrorFallback = Extract<KeycloakClientErrorCode, "createFailed" | "updateFailed">;

const isKeycloakClientErrorCode = (value: string): value is KeycloakClientErrorCode =>
  (KEYCLOAK_CLIENT_ERROR_CODES as readonly string[]).includes(value);

const normalizeMessage = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    const firstString = value.find((item): item is string => typeof item === "string" && item.trim().length > 0);
    return firstString?.trim() ?? "";
  }

  return "";
};

const extractErrorPayload = (err: unknown): { code: string; message: string } => {
  const httpError = err as errorResponse;
  const body = httpError?.error;
  let code = "";
  let message = "";

  if (body && typeof body === "object") {
    const payload = body as { message?: string | string[]; errorMessage?: string; errorType?: string };
    message = normalizeMessage(payload.message) || normalizeMessage(payload.errorMessage) || normalizeMessage(payload.errorType);
  }

  if (!message && typeof httpError?.errorMessage === "string") {
    message = httpError.errorMessage.trim();
  }

  if (!message && typeof httpError?.message === "string") {
    message = httpError.message.trim();
  }

  if (isKeycloakClientErrorCode(message)) {
    code = message;
  }

  return { code, message };
};

export function getServerConnectionErrorKey(err: unknown, fallback: ServerConnectionErrorFallback): string {
  const httpError = err as HttpErrorResponse;
  const { code, message } = extractErrorPayload(err);

  if (code) {
    return `${base}${code}`;
  }

  const lower = message.toLowerCase();

  if (lower.includes("error-user-attribute-read-only") || lower.includes("read-only")) {
    return "errorMessages.readOnlyAttribute";
  }

  if (httpError?.status === 409 || lower.includes("conflict")) {
    if (lower.includes("clientid") || lower.includes("client id") || lower.includes("client_id")) {
      return `${base}clientIdAlreadyExists`;
    }

    if (lower.includes("connectionname") || lower.includes("connection name") || lower.includes("client_name")) {
      return `${base}connectionNameAlreadyExists`;
    }

    if (lower.includes("already exists") || lower.includes("duplicate") || lower === "alreadyexists") {
      return `${base}alreadyExists`;
    }

    if (message) return message;

    return `${base}conflict`;
  }

  return `${base}${fallback}`;
}
